import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, RefreshCw, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function UpcomingVacations() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dispatch/vacations/upcoming`, authHeader());
      setData(r.data);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/dispatch/vacations/sync-now`, {}, authHeader());
      if (r.data?.status === 'error') {
        toast.error(r.data.error || 'Calendar sync failed', { duration: 12000 });
      } else {
        toast.success(`Vacation calendar synced — ${r.data.events} events, ${r.data.matched} matched to employees`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Calendar sync failed', { duration: 12000 });
      load();
    }
    setSyncing(false);
  };

  if (!data) return null;
  const vacs = data.vacations || [];
  const err = data.sync?.status === 'error';
  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4" data-testid="upcoming-vacations">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Plane size={15} className="text-blue-600" /> Upcoming Vacations
        </h3>
        <button onClick={syncNow} disabled={syncing}
          className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="vacations-sync-btn">
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync calendar'}
        </button>
      </div>
      {err && (
        <p className="text-xs text-red-600 mb-2 flex items-start gap-1" data-testid="vacations-sync-error" title={data.sync?.error}>
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Calendar sync failing: {(data.sync?.error || '').slice(0, 140)}{(data.sync?.error || '').length > 140 ? '…' : ''}
        </p>
      )}
      {vacs.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="vacations-empty">No upcoming vacations.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {vacs.map((v, i) => (
            <span key={i} data-testid={`vacation-item-${i}`}
              className="inline-flex items-center gap-1.5 text-xs bg-blue-50 border border-blue-200 text-slate-800 px-2.5 py-1 rounded-md">
              <b>{v.employee_name || 'Unknown'}</b>
              {v.reason && v.reason !== 'Vacation' && <span className="text-orange-700 font-medium">{v.reason}</span>}
              <span className="text-slate-600">{fmt(v.start_date)}{v.end_date !== v.start_date ? ` – ${fmt(v.end_date)}` : ''}</span>
              <span className="text-[10px] text-slate-500">({v.source})</span>
            </span>
          ))}
        </div>
      )}
      {data.sync?.last_sync_at && !err && (
        <p className="text-[10px] text-slate-500 mt-2">Calendar last synced {new Date(data.sync.last_sync_at).toLocaleString()}</p>
      )}
    </div>
  );
}
